import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import * as Cesium from 'cesium';
import { LABEL_FONT_STACKS } from './types';
import { groundedLabelPosition } from '../../utils/cesiumHelpers';
// World-space anchoring: labels store the camera-to-label distance at
// placement time as `referenceDistance`. Each frame we scale the rendered
// label by referenceDistance / currentDistance so it covers the same
// patch of ground regardless of zoom. Beyond CULL_DIST the label hides
// entirely so continent-scale views aren't a wall of tiny text.
const CULL_DIST = 75000; // m — beyond this, hide entirely
const MIN_SCALE = 0.1; // clamp tiny labels so they don't vanish before CULL_DIST
const MAX_SCALE = 8; // clamp huge labels when camera is very close
const computeScale = (referenceDistance, currentDistance) => {
    if (!referenceDistance || referenceDistance <= 0 || currentDistance <= 0)
        return 1;
    const s = referenceDistance / currentDistance;
    if (s < MIN_SCALE)
        return MIN_SCALE;
    if (s > MAX_SCALE)
        return MAX_SCALE;
    return s;
};
// True vector outline via -webkit-text-stroke; paint-order keeps the
// stroke behind the fill so glyphs don't get visually thinned. Replaces
// an older 8-direction text-shadow fake ring that fell apart at the
// large scales produced by world-space anchoring.
const STROKE_CSS = '3px rgba(0,0,0,0.95)';
const rectChanged = (a, b) => {
    if (!a && !b)
        return false;
    if (!a || !b)
        return true;
    return Math.abs(a.left - b.left) > 0.5
        || Math.abs(a.top - b.top) > 0.5
        || Math.abs(a.right - b.right) > 0.5
        || Math.abs(a.bottom - b.bottom) > 0.5;
};
const HANDLE_SIZE = 10;
const HANDLE_HALF = HANDLE_SIZE / 2;
const backgroundFor = (kind) => {
    if (kind === 'white')
        return 'rgba(255,255,255,0.85)';
    if (kind === 'black')
        return 'rgba(0,0,0,0.7)';
    return undefined;
};
const radiusFor = (kind) => {
    if (kind === 'square')
        return 0;
    if (kind === 'pill')
        return 9999;
    return 8; // 'rounded' or undefined → new default
};
const styleForLabel = (label) => {
    var _a;
    const bg = backgroundFor(label.background);
    const hasBg = !!bg;
    const showBorder = hasBg && !!label.backgroundBorder;
    // Vertical padding scales with font size so descenders (notably in
    // serif fonts like Garamond) clear the pill — a fixed 2px choked at
    // large sizes. We also add room for the text stroke ring (3px) when
    // stroke is on, otherwise the stroked cap-tops bleed past the pill's
    // top edge and show as a thin strip above the background.
    // Border on/off compensates -1px so text doesn't shift when toggled.
    // Horizontal padding for Pill mode adds extra room so the curved
    // corners (full-height radius) don't visually clip the first/last
    // glyph; Square/Rounded don't need that part.
    const strokeRoom = label.stroke ? 3 : 0;
    const vBase = Math.max(2, Math.round(label.size * 0.18));
    const padY = hasBg ? (showBorder ? vBase - 1 : vBase) + strokeRoom : 0;
    const baseX = showBorder ? 5 : 6;
    const pillExtra = label.cornerRadius === 'pill' ? Math.round(label.size * 0.35) : 0;
    const padX = hasBg ? baseX + pillExtra + strokeRoom : 0;
    return {
        // Use explicit font properties rather than the `font` shorthand —
        // the shorthand resets unrelated font-* properties (notably
        // fontStyle) and is fussy about declaration order. Explicit
        // properties also give bold + italic toggles a chance to render
        // synthesised glyphs when a system stack doesn't carry the variant.
        fontFamily: (_a = LABEL_FONT_STACKS[label.fontFamily]) !== null && _a !== void 0 ? _a : LABEL_FONT_STACKS.sans,
        fontSize: `${label.size}px`,
        fontWeight: label.bold ? 700 : 400,
        fontStyle: label.italic ? 'italic' : 'normal',
        color: label.color,
        WebkitTextStroke: label.stroke ? STROKE_CSS : undefined,
        paintOrder: label.stroke ? 'stroke fill' : undefined,
        // Background pill — adds a slight padding + rounded backdrop only
        // when enabled, so plain labels keep their tight bounding box. The
        // corner radius preset (square/rounded/pill) is user-selectable.
        backgroundColor: bg,
        padding: hasBg ? `${padY}px ${padX}px` : 0,
        borderRadius: hasBg ? radiusFor(label.cornerRadius) : 0,
        border: showBorder ? `1px solid ${label.color}` : undefined,
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        letterSpacing: '0.01em',
        // `pre` honours `\n` line breaks (Shift+Enter for multi-line labels)
        // AND never soft-wraps at spaces. Previously this was `pre-line`,
        // which collapsed whitespace nicely but allowed the browser to
        // wrap long labels when the unscaled CSS width exceeded the
        // viewport — visible only at large `size` + zoomed-out views, where
        // the scale transform shrunk an already-wrapped layout.
        whiteSpace: 'pre',
        textAlign: 'center'
    };
};
export const LabelMapOverlay = ({ viewer, labels, hiddenIds, onLabelClick, onLabelDoubleClick, onLabelMouseDown, selectedLabelId, onResize, frame, }) => {
    var _a, _b;
    const [positions, setPositions] = useState(() => new Map());
    const labelsRef = useRef(labels);
    useEffect(() => { labelsRef.current = labels; }, [labels]);
    // Tracked DOM refs for each rendered label. Used to measure the selected
    // label's bounding rect so resize handles snap to its visible corners.
    const labelElRefs = useRef(new Map());
    const selectedIdRef = useRef(selectedLabelId);
    useEffect(() => { selectedIdRef.current = selectedLabelId; }, [selectedLabelId]);
    useEffect(() => {
        if (!viewer || viewer.isDestroyed())
            return;
        const update = () => {
            const next = new Map();
            const items = labelsRef.current;
            const cameraPos = viewer.camera.positionWC;
            const selId = selectedIdRef.current;
            for (const l of items) {
                if (!l.position)
                    continue;
                // Anchor by lon/lat on whichever surface is active this frame; the
                // stored height belongs to the terrain mode the label was placed in.
                const anchor = groundedLabelPosition(viewer.scene, l.position);
                // Cull past CULL_DIST — labels stack visually at continent-scale
                // zooms and become unreadable noise.
                const dist = Cesium.Cartesian3.distance(cameraPos, anchor);
                if (dist > CULL_DIST) {
                    next.set(l.id, { x: 0, y: 0, visible: false, scale: 0 });
                    continue;
                }
                const screen = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, anchor);
                if (!screen) {
                    next.set(l.id, { x: 0, y: 0, visible: false, scale: 0 });
                    continue;
                }
                let rect;
                if (l.id === selId) {
                    const el = labelElRefs.current.get(l.id);
                    if (el) {
                        const r = el.getBoundingClientRect();
                        rect = { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
                    }
                }
                next.set(l.id, { x: screen.x, y: screen.y, visible: true, scale: computeScale(l.referenceDistance, dist), rect });
            }
            setPositions(prev => {
                if (prev.size !== next.size)
                    return next;
                for (const [id, p] of next) {
                    const q = prev.get(id);
                    if (!q || q.visible !== p.visible
                        || Math.abs(q.x - p.x) > 0.5
                        || Math.abs(q.y - p.y) > 0.5
                        || Math.abs(q.scale - p.scale) > 0.01
                        || rectChanged(q.rect, p.rect)) {
                        return next;
                    }
                }
                return prev;
            });
        };
        update();
        const remove = viewer.scene.postRender.addEventListener(update);
        return remove;
    }, [viewer]);
    const startResize = (labelId, e) => {
        e.stopPropagation();
        e.preventDefault();
        const label = labelsRef.current.find(l => l.id === labelId);
        const pos = positions.get(labelId);
        if (!label || !pos)
            return;
        const cx = pos.x;
        const cy = pos.y;
        const dx0 = e.clientX - cx;
        const dy0 = e.clientY - cy;
        const dist0 = Math.max(Math.sqrt(dx0 * dx0 + dy0 * dy0), 1);
        const initialSize = label.size;
        const onMove = (ev) => {
            const dxN = ev.clientX - cx;
            const dyN = ev.clientY - cy;
            const distN = Math.sqrt(dxN * dxN + dyN * dyN);
            const ratio = distN / dist0;
            // Clamp to a sane range (matches the size dropdown's intent).
            const clamped = Math.max(8, Math.min(200, Math.round(initialSize * ratio)));
            onResize === null || onResize === void 0 ? void 0 : onResize(labelId, clamped);
        };
        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };
    if (!viewer)
        return null;
    const offsetX = (_a = frame === null || frame === void 0 ? void 0 : frame.x) !== null && _a !== void 0 ? _a : 0;
    const offsetY = (_b = frame === null || frame === void 0 ? void 0 : frame.y) !== null && _b !== void 0 ? _b : 0;
    return (_jsxs("div", { className: frame ? 'pointer-events-none absolute overflow-hidden' : 'pointer-events-none fixed inset-0 z-20', style: frame ? { left: 0, top: 0, width: frame.width, height: frame.height } : undefined, children: [labels.map(label => {
                if (hiddenIds === null || hiddenIds === void 0 ? void 0 : hiddenIds.has(label.id))
                    return null;
                const pos = positions.get(label.id);
                if (!pos || !pos.visible)
                    return null;
                return (_jsx("div", { ref: (el) => {
                        if (el)
                            labelElRefs.current.set(label.id, el);
                        else
                            labelElRefs.current.delete(label.id);
                    }, className: "absolute label-overlay-item", style: {
                        left: pos.x - offsetX,
                        top: pos.y - offsetY,
                        transform: `translate(-50%, -50%) scale(${pos.scale})`,
                        transformOrigin: 'center',
                        pointerEvents: frame ? 'none' : 'auto',
                        cursor: frame ? 'default' : 'pointer',
                        userSelect: 'none',
                        ...styleForLabel(label)
                    }, onClick: (e) => {
                        e.stopPropagation();
                        onLabelClick === null || onLabelClick === void 0 ? void 0 : onLabelClick(label.id);
                    }, onDoubleClick: (e) => {
                        e.stopPropagation();
                        onLabelDoubleClick === null || onLabelDoubleClick === void 0 ? void 0 : onLabelDoubleClick(label.id);
                    }, onMouseDown: (e) => {
                        e.stopPropagation();
                        onLabelMouseDown === null || onLabelMouseDown === void 0 ? void 0 : onLabelMouseDown(label.id, e);
                    }, onWheel: (e) => {
                        if (!viewer || viewer.isDestroyed())
                            return;
                        const canvas = viewer.scene.canvas;
                        const n = e.nativeEvent;
                        canvas.dispatchEvent(new WheelEvent('wheel', {
                            deltaX: n.deltaX,
                            deltaY: n.deltaY,
                            deltaZ: n.deltaZ,
                            deltaMode: n.deltaMode,
                            clientX: n.clientX,
                            clientY: n.clientY,
                            ctrlKey: n.ctrlKey,
                            shiftKey: n.shiftKey,
                            altKey: n.altKey,
                            metaKey: n.metaKey,
                            bubbles: false,
                            cancelable: true,
                        }));
                    }, children: label.text || ' ' }, label.id));
            }), selectedLabelId && onResize && (() => {
                const sel = positions.get(selectedLabelId);
                if (!sel || !sel.visible || !sel.rect)
                    return null;
                const corners = [
                    { x: sel.rect.left, y: sel.rect.top, cursor: 'nwse-resize' },
                    { x: sel.rect.right, y: sel.rect.top, cursor: 'nesw-resize' },
                    { x: sel.rect.left, y: sel.rect.bottom, cursor: 'nesw-resize' },
                    { x: sel.rect.right, y: sel.rect.bottom, cursor: 'nwse-resize' },
                ];
                return corners.map((c, i) => (_jsx("div", { style: {
                        position: 'fixed',
                        left: c.x - HANDLE_HALF,
                        top: c.y - HANDLE_HALF,
                        width: HANDLE_SIZE,
                        height: HANDLE_SIZE,
                        backgroundColor: '#ffffff',
                        border: '1px solid #1f2937',
                        borderRadius: 2,
                        pointerEvents: 'auto',
                        cursor: c.cursor,
                        zIndex: 21,
                    }, onMouseDown: (e) => startResize(selectedLabelId, e), onClick: (e) => e.stopPropagation() }, `handle-${selectedLabelId}-${i}`)));
            })()] }));
};
